import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaLinkedin, FaTwitter, FaFacebookSquare, FaEnvelope, FaPhone, FaMapMarkerAlt, FaAngleRight, FaClock } from 'react-icons/fa';

const FooterContainer = styled.footer`
  background: var(--gradient-primary);
  color: white;
  padding: var(--space-12) 0 0;
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
    background-image: radial-gradient(rgba(255, 255, 255, 0.05) 2px, transparent 2px);
    background-size: 30px 30px;
    pointer-events: none;
    opacity: 0.3;
  }
`;

const FooterTop = styled.div`
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: calc(-1 * var(--space-12));
    left: 0;
    width: 100%;
    height: var(--space-12);
    background: var(--gradient-primary);
    clip-path: polygon(0 100%, 100% 100%, 100% 0, 50% 100%, 0 0);
    z-index: 1;
  }
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--space-8);
  max-width: var(--container-width);
  margin: 0 auto;
  padding: 0 var(--space-6);
  position: relative;
  z-index: 2;
`;

const FooterSection = styled(motion.div)`
  display: flex;
  flex-direction: column;
`;

const FooterTitle = styled.h4`
  font-size: var(--font-size-lg);
  margin-bottom: var(--space-4);
  color: white;
  position: relative;
  padding-bottom: var(--space-2);
  font-weight: 700;
  
  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 40px;
    height: 2px;
    background: var(--accent-color);
    border-radius: 2px;
  }
`;

const FooterLogo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: var(--space-4);
  
  img {
    height: 40px;
    transition: all var(--transition-normal);
  }
`;

const FooterDescription = styled.p`
  line-height: 1.6;
  margin-bottom: var(--space-4);
  color: rgba(255, 255, 255, 0.8);
  font-size: var(--font-size-sm);
`;

const SocialIcons = styled.div`
  display: flex;
  gap: var(--space-2);
  margin-top: var(--space-1);
`;

const SocialIcon = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  transition: all var(--transition-normal);
  font-size: var(--font-size-base);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  &:hover {
    background: var(--accent-color);
    transform: translateY(-3px);
    box-shadow: var(--shadow-lg);
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FooterLink = styled.li`
  margin-bottom: var(--space-2);
  
  a {
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
    transition: all var(--transition-normal);
    display: flex;
    align-items: center;
    font-size: var(--font-size-sm);
    padding: var(--space-1) 0;
    
    &:hover {
      color: var(--accent-color);
      transform: translateX(5px);
    }
    
    svg {
      margin-right: var(--space-2);
      font-size: var(--font-size-xs);
      color: var(--accent-color);
    }
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: var(--space-4);
  color: rgba(255, 255, 255, 0.8);
  font-size: var(--font-size-sm);
  
  svg {
    margin-right: var(--space-3);
    color: var(--accent-color);
    font-size: var(--font-size-base);
    margin-top: var(--space-1);
    flex-shrink: 0;
  }
  
  span {
    line-height: 1.6;
  }
`;

const Newsletter = styled.div`
  margin-top: var(--space-4);
`;

const NewsletterForm = styled.form`
  display: flex;
  margin-top: var(--space-3);
  
  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const NewsletterInput = styled.input`
  flex: 1;
  padding: var(--space-3) var(--space-4);
  border-radius: var(--border-radius-lg) 0 0 var(--border-radius-lg);
  border: none;
  outline: none;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: var(--font-size-sm);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  @media (max-width: 576px) {
    border-radius: var(--border-radius-lg);
    margin-bottom: var(--space-2);
  }
`;

const NewsletterButton = styled(motion.button)`
  padding: var(--space-3) var(--space-5);
  background: var(--accent-color);
  color: white;
  font-weight: 600;
  border: none;
  border-radius: 0 var(--border-radius-lg) var(--border-radius-lg) 0;
  cursor: pointer;
  transition: all var(--transition-normal);
  font-size: var(--font-size-sm);
  
  &:hover {
    box-shadow: var(--shadow-lg);
    transform: translateY(-2px);
    background: var(--accent-dark);
  }
  
  @media (max-width: 576px) {
    border-radius: var(--border-radius-lg);
    width: 100%;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.01) 0%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.01) 100%);
  margin: var(--space-8) 0 0;
`;

const Copyright = styled.div`
  text-align: center;
  padding: var(--space-6) var(--space-6);
  color: rgba(255, 255, 255, 0.6);
  font-size: var(--font-size-xs);
  max-width: var(--container-width);
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: var(--space-3);
  }
`;

const CopyrightText = styled.div``;

const CopyrightLinks = styled.div`
  a {
    color: rgba(255, 255, 255, 0.7);
    margin-left: var(--space-4);
    transition: all var(--transition-normal);
    
    &:hover {
      color: var(--accent-color);
    }
    
    @media (max-width: 768px) {
      margin: 0 var(--space-2);
    }
  }
`;

const BusinessHours = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  
  span {
    display: flex;
    align-items: center;
    font-size: var(--font-size-sm);
    
    svg {
      margin-right: var(--space-2);
      color: var(--accent-color);
    }
  }
`;

const Footer = () => {
  return (
    <FooterContainer>
      <FooterTop />
      <FooterContent>
        <FooterSection
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <FooterLogo>
            <img src="/logo-white.png" alt="Talgo Logo" />
          </FooterLogo>
          <FooterDescription>
            Leading provider of US energy trading compliance solutions with specialized focus on position limits monitoring, CFTC compliance, and regulatory reporting for gas and energy commodity trading firms.
          </FooterDescription>
          <SocialIcons>
            <SocialIcon 
              href="https://linkedin.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              aria-label="LinkedIn"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FaLinkedin />
            </SocialIcon>
            <SocialIcon 
              href="https://twitter.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              aria-label="Twitter"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FaTwitter />
            </SocialIcon>
            <SocialIcon 
              href="https://facebook.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              aria-label="Facebook"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FaFacebookSquare />
            </SocialIcon>
          </SocialIcons>
        </FooterSection>
        
        <FooterSection
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          viewport={{ once: true }}
        >
          <FooterTitle>Quick Links</FooterTitle>
          <FooterLinks>
            <FooterLink>
              <Link to="/"><FaAngleRight />Home</Link>
            </FooterLink>
            <FooterLink>
              <Link to="/about"><FaAngleRight />About Us</Link>
            </FooterLink>
            <FooterLink>
              <Link to="/services"><FaAngleRight />Services</Link>
            </FooterLink>
            <FooterLink>
              <Link to="/case-studies"><FaAngleRight />Case Studies</Link>
            </FooterLink>
            <FooterLink>
              <Link to="/contact"><FaAngleRight />Contact</Link>
            </FooterLink>
          </FooterLinks>
          
          <BusinessHours>
            <FooterTitle>Business Hours</FooterTitle>
            <span><FaClock />Monday - Friday: 9:00 AM - 6:00 PM</span>
            <span><FaClock />Weekend: Closed</span>
          </BusinessHours>
        </FooterSection>
        
        <FooterSection
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <FooterTitle>US Energy Trading Solutions</FooterTitle>
          <FooterLinks>
            <FooterLink>
              <Link to="/services"><FaAngleRight />US Energy Trading Platform</Link>
            </FooterLink>
            <FooterLink>
              <Link to="/services"><FaAngleRight />Position Limits Monitoring</Link>
            </FooterLink>
            <FooterLink>
              <Link to="/services"><FaAngleRight />CFTC Compliance Analytics</Link>
            </FooterLink>
            <FooterLink>
              <Link to="/services"><FaAngleRight />US Energy Trading Consulting</Link>
            </FooterLink>
            <FooterLink>
              <Link to="/services"><FaAngleRight />Gas Trading Data Integration</Link>
            </FooterLink>
          </FooterLinks>
          
          <Newsletter>
            <FooterTitle>US Energy Trading Newsletter</FooterTitle>
            <FooterDescription>
              Subscribe to our newsletter to receive updates on US energy trading compliance, position limits monitoring, CFTC regulations, and gas market insights.
            </FooterDescription>
            <NewsletterForm>
              <NewsletterInput type="email" placeholder="Your email address" />
              <NewsletterButton 
                type="submit"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Subscribe
              </NewsletterButton>
            </NewsletterForm>
          </Newsletter>
        </FooterSection>
        
        <FooterSection
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <FooterTitle>Contact Us</FooterTitle>
          <ContactItem>
            <FaMapMarkerAlt />
            <span>Corporate Headquarters<br />1234 Energy Plaza, Suite 500<br />Houston, Texas 77002, USA</span>
          </ContactItem>
          <ContactItem>
            <FaPhone />
            <span>+1 (832) 555-7890</span>
          </ContactItem>
          <ContactItem>
            <FaEnvelope />
            <span>info@talgo.com</span>
          </ContactItem>
        </FooterSection>
      </FooterContent>
      
      <Divider />
      <Copyright>
        <CopyrightText>&copy; {new Date().getFullYear()} Talgo. All rights reserved.</CopyrightText>
        <CopyrightLinks>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms of Service</Link>
          <Link to="/sitemap">Sitemap</Link>
        </CopyrightLinks>
      </Copyright>
    </FooterContainer>
  );
};

export default Footer; 